/* eslint-disable @typescript-eslint/naming-convention */
import { Location, TitleCasePipe } from "@angular/common";
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { GenericModalComponent } from "src/app/shared/components/generic-modal/generic-modal.component";
import { Operation } from "src/app/shared/types/entity.type";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import { DataTable, GetDataEvent } from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { refreshTab } from "../../../../shared/utils/components/tabs.util";
import { getUpdatedValues, isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { ContractTableService } from "../../contracts/contract-table.service";
import { ContractService } from "../../contracts/contract.service";
import { Contract } from "../../contracts/contract.type";
import { PartnerSpecialTypes } from "../../partners/partner.type";
import { TicketTableService } from "../../tickets/ticket-table.service";
import { TicketService } from "../../tickets/ticket.service";
import { Ticket } from "../../tickets/ticket.type";
import { assetAttributeTypes } from "../asset";
import { AssetAttachmentTableService } from "../asset-attachment-table.service";
import { AssetAttachmentsService } from "../asset-attachments.service";
import { AssetAttributeTableService } from "../asset-attribute-table.service";
import { AssetAttributesService } from "../asset-attributes.service";
import { AssetService } from "../asset.service";
import { Asset, AssetAttachment, AssetAttribute, AssetCoverageStatuses } from "../asset.type";
import { validateDates } from "../assets.shared";
import { CreateAssetService } from "../create-asset/create-asset.service";

@Component({
  selector: "app-manage-asset",
  templateUrl: "./manage-asset.component.html",
  styleUrls: ["./manage-asset.component.css"],
  providers: [CompanyService, CreateAssetService, TitleCasePipe, AssetAttachmentsService, AssetAttributesService],
})
export class ManageAssetComponent implements OnInit {
  @ViewChildren(DataTableComponent) tables: QueryList<DataTableComponent<unknown>>;
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChild(DataTableComponent) dataTableComponent: DataTableComponent<Ticket>;
  @ViewChild("assetAttributesTable") attributesTable: DataTableComponent<AssetAttribute>;
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

  ticketsDataTable: DataTable<Ticket>;

  currentUser: UserInfo;
  manageAssetForm: FormGroup;
  loading = false;
  submitted = false;
  isEdit: boolean;
  asset: Asset;
  currentContract: Contract;
  selectedContract: Contract;
  company: Company;
  disabledContractType = true;
  loading$ = this.loader.loading$;
  questions: QuestionBase<string | boolean>[];
  relatedTickets: Ticket[] = [];
  assetId: string;
  ticketTableConfig: DataTable<Ticket>;
  contractTableConfig: DataTable<Contract>;
  assetAttachmentsTableConfig: DataTable<AssetAttachment>;
  assetAttributesTableConfig: DataTable<AssetAttribute>;
  operation: Operation;
  assetAttributesList: AssetAttribute[];
  defaultAssetAttributeList: AssetAttribute[];

  public isRequiredField = isRequiredField;

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private assetService: AssetService,
    private contractService: ContractService,
    public location: Location,
    public ticketTableService: TicketTableService,
    private router: Router,
    private loader: LoadingService,
    private companyService: CompanyService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private questionService: CreateAssetService,
    private ticketService: TicketService,
    private contractTableService: ContractTableService,
    public assetAttachmentTableService: AssetAttachmentTableService,
    public assetAttributeTableService: AssetAttributeTableService,
    private assetAttachmentsService: AssetAttachmentsService,
    private assetAttributesService: AssetAttributesService,
    public dialogRef: MatDialogRef<GenericModalComponent | DeleteModalComponent>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.loader.show();
  }

  async ngOnInit(): Promise<void> {
    this.assetId = this.route.snapshot.paramMap.get("id");
    this.relatedTickets = (await this.fetchAssignedTickets()).items;
    this.getAsset(this.assetId);
    this.operation = Operation.update;
  }

  getAsset(assetId: string): void {
    this.assetService
      .getAsset(assetId, {
        relations: {
          location: true,
          internalProvider: true,
          sla: true,
          contract: { maintenanceProvider: true },
          company: { partner: true },
        },
      })
      .subscribe(
        async (asset: Asset) => {
          this.asset = asset;
          this.company = asset.company;
          if (asset.contractId) {
            this.currentContract = this.asset.contract;
          }
          if (
            !this.currentContract ||
            !this.currentContract.maintenanceProvider?.isChannelWorks ||
            this.currentUser.isGlobal() ||
            (this.currentUser.isPartner() &&
              this.company.canManageClientPrice &&
              this.currentContract.maintenanceProvider?.isChannelWorks)
          ) {
            this.disabledContractType = false;
          }
          this.buildForm();
          this.setTicketTab();
          this.setContractsTab();
          this.setAttachmentsTab();
          this.setAttributesTab();
        },
        (error) => {
          this.notificationService.error(error || "Couldn't find Asset");
          this.router.navigate(["/assets"]);
        },
      )
      .add(() => this.loader.hide());
  }

  async getCompany(companyId: string): Promise<Company> {
    return await this.companyService.getCompany(companyId).toPromise();
  }

  async getContract(contractId: string): Promise<Contract> {
    return await this.contractService.getContract(contractId, { relations: { maintenanceProvider: true } }).toPromise();
  }

  buildForm(): void {
    const { isFieldEnabled, isSerialNumberEnabled } = this.questionService;

    const isRelutech = this.company.partner.specialType === PartnerSpecialTypes.Relutech;

    this.questionService
      .generateQuestions({
        asset: this.asset,
        currentUser: this.currentUser,
        ...(this.currentUser.isCompany() && {
          partnerSkip: true,
          companySkip: true,
        }),
        ...((this.currentUser.isPartner() || this.currentUser.hasManyCompanies()) && {
          partnerSkip: true,
          companySkip: false,
          companyEdit: false,
        }),
        ...(this.currentUser.isGlobal() && {
          partnerSkip: false,
          partnerEdit: false,
          companySkip: false,
          companyEdit: false,
        }),
        partnerValue: this.asset.company.partnerId,
        companyValue: this.asset.companyId,
        isChannelWorksValue: !this.currentUser.isGlobal() && !this.currentContract?.maintenanceProvider.isChannelWorks,
        partnerPriceHidden: !this.questionService.isPartnerPriceVisible(
          this.currentUser,
          this.company,
          this.currentContract,
        ),
        clientPriceHidden: !this.questionService.isClientPriceVisible(
          this.currentUser,
          this.company,
          this.currentContract,
        ),
        serialNumberEdit: isSerialNumberEnabled(this.currentUser, this.disabledContractType, this.relatedTickets),
        nameEdit: isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
        descriptionEdit: isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
        coverageStatusEdit: false,
        startDateEdit: false,
        endDateEdit: false,
        typeEdit: isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
        slaEdit: false,
        oemEdit: isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
        modelEdit: isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
        contractIdEdit: false,
        partnerPriceEdit: false,
        clientPriceEdit: false,
        locationIdEdit:
          this.questionService.isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
        channelWorksCostEdit: false,
        monitoringEdit: false,
        dmrEdit: false,
        hideContractNumber: false,
        hideQuotedSla: !isRelutech,
        hideQuotedPrice: !isRelutech,
        hideQuotedTerm: !isRelutech,
        quotedSlaEdit: !this.currentUser.isCompany(),
        quotedPriceEdit: !this.currentUser.isCompany(),
        quotedTermEdit: !this.currentUser.isCompany(),
        internalProviderEdit: false,
        showFmvValue: this.company.enableAssetCustomFields,
        showBookValue: this.company.enableAssetCustomFields,
        fmvValueEdit: this.company.enableAssetCustomFields && this.currentUser.isGlobalAdmin(),
        bookValueEdit: this.company.enableAssetCustomFields && this.currentUser.isGlobalAdmin(),
        operation: this.operation,
      })
      .subscribe((qs) => {
        this.questions = qs;
      });
  }

  async onChange(event: any): Promise<void> {
    validateDates(event, this.dynamicForm);

    if (event.source?.id === "companyId") {
      const companyId = event.value;
      this.company = await this.getCompany(companyId);
      if (this.company.enableAssetCustomFields && this.currentUser.isGlobalAdmin()) {
        this.dynamicForm.form.controls["fairMarketValue"].enable();
        this.dynamicForm.form.controls["bookValue"].enable();
      } else {
        this.dynamicForm.form.controls["fairMarketValue"].disable();
        this.dynamicForm.form.controls["bookValue"].disable();
      }
    }
    if (event.source?.id === "contractId") {
      const contractId = event.value;
      this.selectedContract = await this.getContract(contractId);

      const { isPartnerPriceVisible, isPartnerPriceEnabled, isClientPriceVisible, isClientPriceEnabled } =
        this.questionService;

      // partner price
      // handle visibility
      if (isPartnerPriceVisible(this.currentUser, this.company, this.selectedContract)) {
        this.dynamicForm.getQuestion("partnerPrice").hide = false;
      } else {
        this.dynamicForm.getQuestion("partnerPrice").hide = true;
      }
      // handle enabled/disabled
      if (isPartnerPriceEnabled(this.currentUser, this.company, this.selectedContract)) {
        this.dynamicForm.form.controls["partnerPrice"].enable();
      } else {
        this.dynamicForm.form.controls["partnerPrice"].disable();
      }
      // client price
      // handle visibility
      if (isClientPriceVisible(this.currentUser, this.company, this.selectedContract)) {
        this.dynamicForm.getQuestion("clientPrice").hide = false;
      } else {
        this.dynamicForm.getQuestion("clientPrice").hide = true;
      }
      // handle enabled/disabled
      if (isClientPriceEnabled(this.currentUser, this.company, this.selectedContract)) {
        this.dynamicForm.form.controls["clientPrice"].enable();
      } else {
        this.dynamicForm.form.controls["clientPrice"].disable();
      }

      // toggle contract number visibility
      this.dynamicForm.replaceOrAppendQuestion(
        this.questionService.createContractNumberQuestion({ contract: this.selectedContract }),
      );
    }

    if (event.source?.id === "coverageStatus") {
      const coverageStatusValue = event.value;
      const { isFieldEnabled, createDateQuestion } = this.questionService;
      this.dynamicForm.replaceOrAppendQuestion(
        createDateQuestion({
          asset: this.asset,
          coverageStatusValue,
          startDateEdit: isFieldEnabled(this.currentUser, this.currentContract) && !this.disabledContractType,
          resetValue: coverageStatusValue === AssetCoverageStatuses.pending,
        }),
      );
    }
  }

  onSubmit(): void {
    this.updateAsset();
  }

  goBack(): void {
    this.location.back();
  }

  updateAsset(): void {
    const data: Asset = getUpdatedValues(this.dynamicForm.form, this.asset);

    // workaround to handle partner price: it deletes the partner price when
    // the contract changes to a not channel works, and the user is a global user
    if (this.selectedContract) {
      if (!this.selectedContract?.maintenanceProvider.isChannelWorks && this.currentUser.isGlobal()) {
        delete data["partnerPrice"];
      }
    }

    // workaround for an issue that happens when an empty field (price/unmanaged) is sent
    if ((data.partnerPrice as any) === "") {
      data.partnerPrice = null;
    }
    if ((data.clientPrice as any) === "") {
      data.clientPrice = null;
    }

    if (data.startDate && !data.endDate) {
      data.endDate = this.asset.endDate;
    }

    if (data.endDate && !data.startDate) {
      data.startDate = this.asset.startDate;
    }

    if (data) {
      this.loader.show();
      this.assetService
        .updateAsset(this.asset.id, data)
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          (asset: Asset) => {
            this.asset = asset;
            this.toggleManageAsset();
            this.notificationService.success("Asset updated");
            if (data.category) {
              this.refreshAddAssetAttributeButton(asset);
            }

            this.attributesTable.reset(true);
          },
          (_error) => {
            this.notificationService.error(_error || "Could not update Asset");
          },
        )
        .add(() => this.loader.hide());
    }
  }

  toggleManageAsset(): void {
    this.isEdit = !this.isEdit;
  }

  async deleteAssetModal(): Promise<void> {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "asset",
        data: this.asset,
        url: this.assetService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/assets"]);
      }
    });
  }

  canEditAsset(): boolean {
    if (
      !this.disabledContractType &&
      (this.currentUser.isGlobal() ||
        (!this.currentUser.isGlobal() && !this.asset?.contract?.maintenanceProvider?.isChannelWorks))
    )
      return allowUpdateEntity(this.asset, this.currentUser);

    return false;
  }

  canDeleteAsset(): boolean {
    if (
      this.currentUser.isGlobal() ||
      (!this.currentUser.isGlobal() && !this.asset?.contract?.maintenanceProvider?.isChannelWorks)
    )
      return allowDeleteEntity(this.asset, this.currentUser);
    return false;
  }

  onTabChange = (): void => this.resetTabs();

  //#region Attachment Tab

  /** Populate attachments table */
  getAssetAttachments = (event: GetDataEvent<AssetAttachment>): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getAssetAttachments(this.assetId, {
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate attributes table */
  getAssetAttributes = (event: GetDataEvent<AssetAttribute>): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getAssetAttributes(this.assetId, {
        ...queryParams,
        relations: {
          attribute: true,
        },
        where: [
          { assetId: this.assetId, ...queryParams.where },
          { assetTypeId: this.asset.typeId, category: this.asset.category, default: true, ...queryParams.where },
        ],
        order: {},
        pagination: {
          pageSize: 100,
          page: 1,
        },
      })
      .subscribe(
        (data): void => {
          this.defaultAssetAttributeList = data.items;
          const deletedAttributes = data.items.filter((item) => item.deleted);
          // for each deletedAttribute
          deletedAttributes.forEach((deletedAttribute) => {
            data.items = data.items.filter(
              (item) => !(item.attributeId === deletedAttribute.attributeId && item.default && !item.deleted),
            );
          });

          // if current user is not global then hide deleted attributes
          if (!this.currentUser.isGlobal()) {
            data.items = data.items.filter((item) => !item.deleted);
          }

          this.assetAttributesList = data.items;
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };
  //#endregion

  private setAttachmentsTab = (): void => {
    this.assetAttachmentsTableConfig = this.assetAttachmentTableService.getAssetAttachmentsTableConfig();
    this.assetAttachmentsTableConfig = {
      ...this.assetAttachmentsTableConfig,
      toolBar: {
        ...this.assetAttachmentsTableConfig.toolBar,
        addItem: {
          function: this.addAttachment,
        },
      },
    };
  };

  private addAttachment = (table: DataTableComponent<AssetAttachment>): void => {
    const questions: QuestionBase<string | boolean>[] = [];

    this.assetAttachmentsService
      .generateQuestions({
        isDescriptionEditable: true,
        hideFile: false,
        isNameEditable: true,
      })
      .subscribe((qs) => {
        Object.assign(questions, qs);
      });

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      restoreFocus: false,
      data: {
        title: "Add Asset Attachment",
        message: "Documents uploaded are viewable by all users in the organization",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: AssetAttachment) => {
                this.createAttachment(values, table);
              },
            },
          ],
        },
      },
    });
  };

  private createAttachment = (data: AssetAttachment, table: DataTableComponent<AssetAttachment>): void => {
    this.loader.show();
    const extension = data.file.name.split(".").pop();
    this.assetService.getS3Url(this.asset.id, extension).subscribe(
      (observer) => {
        this.assetAttachmentsService.uploadFile(observer.url, data.file).subscribe(
          () => {
            this.assetService
              .createAttachment({
                name: data.name,
                description: data.description,
                url: observer.name,
                assetId: this.asset.id,
              })
              .subscribe(
                () => {
                  this.loader.hide();
                  this.notificationService.success("Attachment added successfully");
                  this.dialogRef.close();
                  table.search();
                  this.contractService.getContractAttachments(this.asset.id);
                },
                (_err) => {
                  this.loader.hide();
                  this.notificationService.error(`Could not create attachment ${_err}`);
                },
              );
          },
          () => {
            this.loader.hide();
            this.notificationService.error("Could not create attachment");
          },
        );
      },
      () => {
        this.loader.hide();
        this.notificationService.error("Could not create attachment");
        table.search();
      },
    );
  };

  private setAttributesTab = (): void => {
    this.assetAttributesTableConfig = this.assetAttributeTableService.getAssetAttributesTableConfig({
      entities: { asset: this.asset },
    });
    this.assetAttributesTableConfig = {
      ...this.assetAttributesTableConfig,
      paginator: {
        hidden: true,
      },
      toolBar: {
        ...this.assetAttributesTableConfig.toolBar,
        refreshTable: { hidden: false },
        addItem: {
          function: this.addAttribute,
          // hide if asset category doesn't match with calculator categories to load types
          hidden: !assetAttributeTypes.filter((type) => type.auxValue === this.asset.category).length,
        },
      },
    };
  };

  private addAttribute = (table: DataTableComponent<AssetAttribute>): void => {
    const questions: QuestionBase<string | boolean>[] = [];

    this.assetAttributesService
      .generateQuestions({
        asset: this.asset,
        assetAttributes: this.assetAttributesList,
      })
      .subscribe((qs) => {
        Object.assign(questions, qs);
      });

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      restoreFocus: false,
      data: {
        title: "Create New Attribute",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: AssetAttribute) => {
                this.createAttribute(values, table);
              },
            },
          ],
        },
      },
    });
  };

  private createAttribute = (data: AssetAttribute, table: DataTableComponent<AssetAttribute>): void => {
    this.loader.show();
    this.assetService
      .createAssetAttribute({
        quantity: data.quantity,
        assetId: this.asset.id,
        attributeId: data.attributeId,
        deleted: false,
      })
      .subscribe(
        () => {
          this.notificationService.success("Asset attribute created successfully");
          this.dialogRef.close();
          table.search();
        },
        (_err) => {
          this.notificationService.error(`Could not create asset attribute ${_err}`);
        },
      )
      .add(() => this.loader.hide());
  };

  //#endregion

  private resetTabs = (): void => {
    setTimeout(() => this.tables.forEach((table) => table.resetScrollbar()), 0);
  };

  private async fetchAssignedTickets(): Promise<PaginatedResponse<Ticket>> {
    return this.ticketService.getTickets({ where: { assetId: this.assetId } }).toPromise();
  }

  private setTicketTab() {
    this.ticketTableConfig = this.ticketTableService.getTicketsTableConfig({
      entities: { asset: this.asset },
      hidden: ["serialNumber"],
    });
  }

  private getTickets = (event: GetDataEvent<Ticket>): void => this.ticketTableService.getTickets(event, this.assetId);

  private getContracts = (event: GetDataEvent<Contract>): void =>
    this.contractTableService.getContracts(event, this.assetId);

  private setContractsTab() {
    this.contractTableConfig = this.contractTableService.getContractsTableConfig({
      entities: { asset: this.asset },
    });
  }

  private refreshAddAssetAttributeButton(asset: Asset): void {
    this.assetAttributesTableConfig.toolBar.addItem.hidden = !assetAttributeTypes.filter(
      (type) => type.auxValue === asset.category,
    ).length;
    // we need to refresh the whole tab to show or hide the button
    refreshTab(this.tabGroup);
  }
}
